/**
 * « Libérer des créneaux » : ce que `<TutorReleaseSlotsPage>` montre, lu dans le store (patron A : la
 * semaine et l'aperçu sont rangés par le réducteur `tutorAvailability`) et dans le brouillon de
 * l'écran (les créneaux marqués, la semaine retouchée).
 *
 * Port de `ReleaseSlotsScreen.js` : seuls les créneaux encore engagés comptent dans la sélection (un
 * créneau libéré entre-temps ne part pas) ; un créneau marqué est barré et un second toucher le rend ;
 * l'aperçu n'est lu que s'il a été demandé par CE parcours (la semaine et le dernier aperçu sont
 * persistés).
 *
 * Un `createSelector` : chaque champ du brouillon est une entrée à lui (`ports/CLAUDE.md`).
 */
import { createSelector } from "reselect"

import {
    type ApiReleasePreview,
    engagedSlotLabel,
    toUITutorReleasePreview,
} from "../../../adapters/tutor/Availability/tutorReleaseAdapter"
import {
    type ApiTutorWeekView,
    engagedSlotsOf,
    openSlotsOf,
    releasedSlotsOf,
    slotKeyOf,
    toUITutorWeekDays,
    toWeekSlot,
} from "../../../adapters/tutor/shared/tutorWeekAdapter"
import { lowerFirst } from "../../../ui/formatters/textFormatter"
import { type UITutorReleasePreview, type UITutorWeekDay } from "../../../ui/types/tutorDispoOffres"
import { selectAccount } from "../../shared/displayedStudent"

/** Le brouillon de l'écran : ce que le tuteur a touché. */
export type TutorReleaseDraft = {
    /** Les clés des créneaux engagés marqués. */
    selectedKeys: string[]
    /** Le créneau d'où part le geste (une heure de « Ma dispo », un live). */
    preselectedKey: string | null
    /** Les créneaux proposés retouchés ; `null` = la semaine servie. */
    draftOpenSlots: string[] | null
    /** L'aperçu affiché est celui que ce parcours a demandé. */
    previewAsked: boolean
    /** Ouvert depuis un essai ou un stage : « essai du mardi à 17h ». */
    oneOffLabel: string | null
}

export type TutorReleaseSlotsViewModel = {
    days: UITutorWeekDay[] | null
    /** Les clés marquées encore engagées : ce que le corps envoie. */
    selected: string[]
    serverOpenSlots: string[]
    preselectedLabel?: string
    canContinue: boolean
    preview: UITutorReleasePreview | null
    tutorFirstname?: string
    loading: boolean
    error: unknown
}

type ReleaseState = {
    data?: {
        tutorAvailability?: { weekView?: ApiTutorWeekView | null; releasePreview?: ApiReleasePreview | null } | null
    }
    api?: { getAvailability?: { error?: unknown } | null }
}

const root = (state: unknown) => state as ReleaseState

const selectRelease = createSelector(
    [
        (state: unknown) => root(state)?.data?.tutorAvailability?.weekView ?? null,
        (state: unknown) => root(state)?.data?.tutorAvailability?.releasePreview ?? null,
        (state: unknown) => root(state)?.api?.getAvailability?.error ?? null,
        (state: unknown) => (selectAccount(state as never)?.firstname as string | undefined) ?? undefined,
        (_state: unknown, draft: TutorReleaseDraft) => draft.selectedKeys,
        (_state: unknown, draft: TutorReleaseDraft) => draft.preselectedKey,
        (_state: unknown, draft: TutorReleaseDraft) => draft.draftOpenSlots,
        (_state: unknown, draft: TutorReleaseDraft) => draft.previewAsked,
        (_state: unknown, draft: TutorReleaseDraft) => draft.oneOffLabel,
    ],
    (
        week,
        rawPreview,
        loadError,
        firstname,
        selectedKeys,
        preselectedKey,
        draftOpenSlots,
        previewAsked,
        oneOffLabel
    ): TutorReleaseSlotsViewModel => {
        const engaged = engagedSlotsOf(week)
        const engagedKeys = engaged.map((slot) => slotKeyOf(slot.slot as string))
        const selected = selectedKeys.filter((key) => engagedKeys.includes(key))
        const serverOpenSlots = openSlotsOf(week)
        const openSlots = draftOpenSlots ?? serverOpenSlots
        const slots = [
            ...openSlots.map((slot) => ({ slot, engaged: false })),
            ...engaged.map((slot) =>
                selected.includes(slotKeyOf(slot.slot as string))
                    ? toWeekSlot(slot, { freed: true, releasable: true })
                    : toWeekSlot(slot)
            ),
            ...releasedSlotsOf(week).map((slot) => toWeekSlot(slot, { freed: true })),
        ]
        const preselectedSlot = engaged.find((slot) => slotKeyOf(slot.slot as string) === preselectedKey)
        const preselectedLabel =
            preselectedSlot != null && preselectedKey != null && selected.includes(preselectedKey)
                ? lowerFirst(engagedSlotLabel(preselectedSlot))
                : (oneOffLabel ?? undefined)
        return {
            days: week == null ? null : toUITutorWeekDays(week.grid, slots),
            selected,
            serverOpenSlots,
            preselectedLabel,
            canContinue: selected.length > 0 || oneOffLabel != null,
            preview: previewAsked ? toUITutorReleasePreview(rawPreview) : null,
            tutorFirstname: firstname,
            loading: week == null && loadError == null,
            error: week == null ? loadError : null,
        }
    }
)

export function tutorReleaseSlotsViewModel(state: unknown, draft: TutorReleaseDraft): TutorReleaseSlotsViewModel {
    return selectRelease(state, draft)
}
